import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import axios from "axios";
import TuvSud from "../images/tuv-sud.png";
import "./Agents.css";

// Simple shimmer placeholder component
const ShimmerCard = () => (
  <Box
    sx={{
      height: 220,
      width: "100%",
      bgcolor: "grey.300",
      borderRadius: 2,
      animation: "shimmer 1.5s infinite linear",
      background:
        "linear-gradient(to right, #eeeeee 0%, #dddddd 50%, #eeeeee 100%)",
      backgroundSize: "200% 100%",
      "@keyframes shimmer": {
        "0%": { backgroundPosition: "-200% 0" },
        "100%": { backgroundPosition: "200% 0" },
      },
    }}
  />
);

function ResponsiveTabs({ categories, selectedTab, setSelectedTab }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Box
        display="flex"
        justifyContent="space-between"
        mt={2}
        sx={{ width: "100%" }}
      >
        {categories.map((cat, i) => (
          <Box
            key={i}
            onClick={() => setSelectedTab(i)}
            sx={{
              flex: 1,
              textAlign: "center",
              py: 1,
              mx: 0.5,
              borderRadius: 1,
              cursor: "pointer",
              backgroundColor:
                selectedTab === i
                  ? "rgba(0,51,161,0.8)"
                  : "rgba(255,255,255,0.1)",
              color: selectedTab === i ? "white" : "rgba(0,51,161,0.4)",
              fontWeight: selectedTab === i ? "bold" : "normal",
              transition: "0.3s",
              whiteSpace: "nowrap",
            }}
          >
            {cat}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" mt={2} width="100%">
      <Tabs
        value={selectedTab}
        onChange={(e, val) => setSelectedTab(val)}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        TabIndicatorProps={{ style: { height: 3, background: "#0033A1" } }}
        sx={{ "& .MuiTab-root": { fontSize: "0.85rem", whiteSpace: "nowrap" } }}
      >
        {categories.map((cat, i) => (
          <Tab key={i} label={cat} wrapped={false} />
        ))}
      </Tabs>
    </Box>
  );
}

export default function Agents() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const categories = ["Всички", "Соларни панели", "Фасади", "Индустриални"];

  // Restore tab from AgentInfo if coming back
  useEffect(() => {
    if (location.state?.selectedTab !== undefined) {
      setSelectedTab(location.state.selectedTab);
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    axios
      .get("/db/agents.json")
      .then((res) => setAgents(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const filteredAgents =
    selectedTab === 0
      ? agents
      : agents.filter((a) => a.category === categories[selectedTab]);

  const scrollLeft = () =>
    scrollRef.current?.scrollBy({ left: -180, behavior: "smooth" });
  const scrollRight = () =>
    scrollRef.current?.scrollBy({ left: 180, behavior: "smooth" });

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress size={60} sx={{ color: "#0033A1" }} />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center" mt={4}>
        Грешка при зареждането на препаратите!
      </Typography>
    );

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 4,
        maxWidth: 1400,
        mx: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        flexDirection="column"
        mx="auto"
        maxWidth={700}
        mb={3}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          mb={3}
          sx={{ color: "#2f4f7f" }}
        >
          ПРЕПАРАТИ ЗА ПОЧИСТВАНЕ
        </Typography>
        <Typography variant="body1" mb={1}>
          Препаратите за почистване на <b>ChemiТek</b> допринасят за повишаване
          на производителността на Вашите соларни панели.
        </Typography>
        <Typography variant="body1">
          Като официален представител на <b>ChemiТek</b> за България, ние
          предоставяме за продажба следните продукти:
        </Typography>
      </Box>

      {/* Tabs */}
      <ResponsiveTabs
        categories={categories}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      {/* Product Cards */}
      {isMobile ? (
        <Box sx={{ position: "relative", mt: 3 }}>
          <IconButton
            onClick={scrollLeft}
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.3)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
          >
            <ArrowBackIosIcon sx={{ color: "#fff" }} />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              overflowX: "auto",
              gap: 2,
              px: 1,
              pb: 2,
              scrollSnapType: "x mandatory",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {filteredAgents.length > 0
              ? filteredAgents.map((agent) => (
                  <Card
                    key={agent.id}
                    sx={{
                      minWidth: 150,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      scrollSnapAlign: "center",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "scale(1.05)" },
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                  >
                    <NavLink
                      to={`/agentInfo/${agent.abb}`}
                      state={{ agent, tab: selectedTab }}
                    >
                      <CardMedia
                        component="img"
                        image={agent.image}
                        title={agent.nameBulgarian}
                        sx={{
                          height: 180,
                          width: 140,
                          margin: "auto",
                          borderRadius: 1,
                        }}
                      />
                    </NavLink>
                    <CardContent>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        align="center"
                      >
                        {agent.nameBulgarian}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              : Array.from({ length: 4 }).map((_, i) => (
                  <ShimmerCard key={i} />
                ))}
          </Box>

          <IconButton
            onClick={scrollRight}
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.3)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
          >
            <ArrowForwardIosIcon sx={{ color: "#fff" }} />
          </IconButton>
        </Box>
      ) : (
        <Grid container spacing={4} mt={3} justifyContent="center">
          {filteredAgents.length > 0
            ? filteredAgents.map((agent) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                  <Card
                    elevation={3}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "scale(1.03)" },
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                  >
                    <NavLink
                      to={`/agentInfo/${agent.abb}`}
                      state={{ agent, tab: selectedTab }}
                    >
                      <CardMedia
                        component="img"
                        image={agent.image}
                        title={agent.nameBulgarian}
                        sx={{
                          height: 220,
                          width: "100%",
                          objectFit: "contain",
                          borderRadius: 1,
                        }}
                      />
                    </NavLink>
                    <CardContent>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        align="center"
                      >
                        {agent.nameBulgarian}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            : Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <ShimmerCard />
                </Grid>
              ))}
        </Grid>
      )}

      {/* TÜV info */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt={4}
        flexWrap="wrap"
      >
        <img src={TuvSud} height="60px" width="60px" alt="tuv" />
        <Typography variant="body2" align="center" ml={1}>
          Всички препарати са биоразградими, с неутрално pH и сертификат TÜV
          SÜD.
        </Typography>
      </Box>
    </Box>
  );
}
